<template>
  <div class="bereiche-list container py-4">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h1><font-awesome-icon icon="users" /> Meine Bereiche</h1>
      <div class="d-flex gap-2" v-if="requests.length > 0">
        <button class="btn btn-primary text-white" @click="navigateToRequests">
          <font-awesome-icon icon="clipboard-list" /> Meine Beitrittsanfragen
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Lade Bereiche...</span>
      </div>
    </div>

    <div v-else-if="error" class="alert alert-danger" role="alert">
      <font-awesome-icon icon="exclamation-triangle" /> {{ error }}
    </div>



    <div v-else class="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
      <!-- Bereich Aktionen Card - immer sichtbar -->
      <div class="col">
        <div class="card h-100 border-dashed">
          <!-- Neuen Bereich erstellen -->
          <div class="card-body d-flex flex-column justify-content-center align-items-center text-center py-3 border-bottom" @click="navigateToCreate" style="cursor: pointer;">
            <div class="mb-2">
              <font-awesome-icon icon="plus-circle" size="2x" class="text-primary" />
            </div>
            <h6 class="card-title text-primary mb-1">Neuen Bereich erstellen</h6>
            <p class="card-text text-muted small mb-0">
              Bereich erstellen und Mitglieder einladen
            </p>
          </div>
          
          <!-- Bereich beitreten -->
          <div class="card-body d-flex flex-column justify-content-center align-items-center text-center py-3" @click="showJoinModal" style="cursor: pointer;">
            <div class="mb-2">
              <font-awesome-icon icon="user-plus" size="2x" class="text-success" />
            </div>
            <h6 class="card-title text-success mb-1">Bereich beitreten</h6>
            <p class="card-text text-muted small mb-0">
              Mit Einladungscode beitreten
            </p>
          </div>
        </div>
      </div>
      
      <!-- Bereiche Grid - server-seitig paginiert -->
      <div v-for="team in paginatedTeams" :key="team.id" class="col">
        <div
          class="card h-100 team-card-clickable"
          :class="{ 'border-warning': hasMissingRequiredFields(team) }"
          :style="
            hasMissingRequiredFields(team) ? 'background-color: var(--bg-warning)' : ''
          "
          @click="viewTeam(team)"
        >
          <div class="card-body pt-3 ps-3 pe-3 pb-0 team-card-body">
            <div class="team-card-content">
              <div>
                <div class="d-flex justify-content-between align-items-start team-title mb-2">
                  <div class="d-flex align-items-center me-2" style="min-width: 0; flex: 1;">
                    <div v-if="isProTeam(team)" class="me-2 flex-shrink-0">
                      <font-awesome-icon 
                        icon="crown" 
                        class="text-warning" 
                        size="sm"
                        title="Pro Bereich"
                      />
                    </div>
                    <h5 class="card-title mb-0 team-card-title" :title="team.name">{{ team.name }}</h5>
                  </div>
                  <span
                    class="badge rounded-pill flex-shrink-0"
                    :class="
                      team.creatorId === user?.id
                        ? 'bg-danger'
                        : team.role === 'teamAdmin'
                          ? 'bg-primary'
                          : 'bg-secondary'
                    "
                    style="white-space: nowrap;"
                  >
                    {{
                      team.creatorId === user?.id
                        ? 'Bereichsersteller'
                        : team.role === 'teamAdmin'
                          ? 'Administrator'
                          : 'Mitglied'
                    }}
                  </span>
                </div>
                <p class="card-text team-card-description mb-2" :title="team.description">{{ team.description }}</p>
                
                <!-- Quittungen Display -->
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <div class="d-flex align-items-center">
                    <font-awesome-icon icon="coins" :class="getCoinsIconClass(team.account?.amount || 0)" class="me-2" />
                    <span class="fw-bold" :class="getCoinsTextClass(team.account?.amount || 0)">
                      {{ formatCoins(team.account?.amount || 0) }} Quittungen
                    </span>
                  </div>
                  <div
                    :class="[
                      'team-logo-list-container',
                      'ms-2',
                      { 'team-logo-list-container--has-logo': hasTeamLogo(team) }
                    ]"
                  >
                    <img
                      v-if="hasTeamLogo(team)"
                      :src="getLogoUrl(team)"
                      :alt="`${team.name} Logo`"
                      class="team-logo-list"
                      :data-team-id="String(team.id)"
                      @error="handleLogoError"
                    />
                    <div
                      v-else
                      class="team-logo-placeholder team-logo-placeholder--empty"
                    ></div>
                  </div>
                </div>
              </div>
              
              <div class="d-flex justify-content-between align-items-center">
                <small class="text-muted"
                  >{{ team.member?.length || 0 }}
                  {{ team.member?.length > 1 ? 'Mitglieder' : 'Mitglied' }}</small
                >
              </div>
            </div>
          </div>
          <div class="card-footer team-card-footer">
            <div
              class="btn  w-100 position-relative text-center"
              :class="hasMissingRequiredFields(team) ? 'btn-warning' : 'btn-primary'"
            >
              <font-awesome-icon
                :icon="hasMissingRequiredFields(team) ? 'exclamation-circle' : 'eye'"
              />
              {{
                hasMissingRequiredFields(team) ? 'Pflichtfelder ausfüllen' : 'Details anzeigen'
              }}
              
              <!-- Pending Requests Badge -->
              <span 
                v-if="showPendingBadge(team)" 
                class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
              >
                {{ team.pendingRequestsCount > 99 ? '99+' : team.pendingRequestsCount }}
                <span class="visually-hidden">ausstehende Anfragen</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination - only show when there are teams and multiple pages -->
    <div v-if="teams.length > 0 && pagination.totalPages > 1" class="pagination-container mt-4">
      <nav aria-label="Bereich Pagination">
        <ul class="pagination justify-content-center">
          <li class="page-item" :class="{ disabled: pagination.page === 1 }">
            <a
              class="page-link"
              href="#"
              @click.prevent="handlePageChange(pagination.page - 1)"
            >
              <font-awesome-icon icon="chevron-left" /> Zurück
            </a>
          </li>

          <template v-if="pagination.totalPages <= 7">
            <li
              v-for="page in pagination.totalPages"
              :key="page"
              class="page-item"
              :class="{ active: page === pagination.page }"
            >
              <a class="page-link" href="#" @click.prevent="handlePageChange(page)">
                {{ page }}
              </a>
            </li>
          </template>

          <template v-else>
            <li class="page-item" :class="{ active: pagination.page === 1 }">
              <a class="page-link" href="#" @click.prevent="handlePageChange(1)">1</a>
            </li>

            <li v-if="pagination.page > 3" class="page-item disabled">
              <span class="page-link">...</span>
            </li>

            <li
              v-for="page in getPageNumbers()"
              :key="page"
              class="page-item"
              :class="{ active: page === pagination.page }"
            >
              <a class="page-link" href="#" @click.prevent="handlePageChange(page)">
                {{ page }}
              </a>
            </li>

            <li v-if="pagination.page < pagination.totalPages - 2" class="page-item disabled">
              <span class="page-link">...</span>
            </li>

            <li class="page-item" :class="{ active: pagination.page === pagination.totalPages }">
              <a
                class="page-link"
                href="#"
                @click.prevent="handlePageChange(pagination.totalPages)"
              >
                {{ pagination.totalPages }}
              </a>
            </li>
          </template>

          <li class="page-item" :class="{ disabled: pagination.page === pagination.totalPages }">
            <a
              class="page-link"
              href="#"
              @click.prevent="handlePageChange(pagination.page + 1)"
            >
              Weiter <font-awesome-icon icon="chevron-right" />
            </a>
          </li>
        </ul>
      </nav>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useTeamsStore } from '@/stores/teams'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { teamService } from '@/api/teams'
import { showError } from '@/services/notification'


const authStore = useAuthStore()
const teamsStore = useTeamsStore()
const router = useRouter()


const { user } = storeToRefs(authStore)
const { 
  teams, 
  pagination, 
  loading, 
  error,

} = storeToRefs(teamsStore)
const { 
  handlePageChange,
  selectTeam
} = teamsStore


const pendingRequests = ref([])
const loadingRequests = ref(false)
const requests = ref([])

const pastRequests = ref([])
const loadingHistory = ref(false)

const viewTeam = (team) => {
  selectTeam(team)
  router.push({ name: 'teams-detail', params: { id: team.id } })
}


const navigateToCreate = () => {
  router.push({ name: 'teams-create' })
}

const navigateToRequests = () => {
  router.push({ name: 'teams-requests' })
}

const showJoinModal = () => {
  router.push({ name: 'teams-join' })
}
const formatCoins = (amount) => {
  if (!amount || amount === 0) return '0'
  return new Intl.NumberFormat('de-DE').format(amount)
}

const logoUrls = ref(new Map())

const handleLogoError = (event) => {
  const teamKey = event.target.dataset.teamId
  if (teamKey && logoUrls.value.has(teamKey)) {
    const url = logoUrls.value.get(teamKey)
    if (url) {
      URL.revokeObjectURL(url)
    }
    logoUrls.value.delete(teamKey)
  }
  event.target.style.display = 'none'
}

const isProTeam = (team) => {
  return team.status === 'pro' || team.status === 'ultimate'
}

const loadLogoIfNeeded = async (teamId) => {
  const key = String(teamId)
  if (!logoUrls.value.has(key)) {
    try {
      const logoBlob = await teamService.getTeamLogo(teamId)
      const url = URL.createObjectURL(logoBlob)
      logoUrls.value.set(key, url)
    } catch (error) {
      console.warn(`Logo für Bereich ${teamId} konnte nicht geladen werden:`, error)
    }
  }
}

const getCoinsIconClass = (amount) => {
  if (amount === 0) return 'text-danger'
  if (amount < 10) return 'text-warning'
  return 'text-success'
}

const getCoinsTextClass = (amount) => {
  if (amount === 0) return 'text-danger'
  if (amount < 10) return 'text-warning'
  return 'text-success'
}

const hasTeamLogo = (team) => {
  if (!team || !isProTeam(team)) return false
  if (!team.receiptFooterImage) return false
  return logoUrls.value.has(String(team.id))
}

const getLogoUrl = (team) => {
  if (!team) return ''
  return logoUrls.value.get(String(team.id)) || ''
}


const getPageNumbers = () => {
  const current = pagination.page
  const total = pagination.totalPages
  const pages = []

  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  if (current <= 3) {
    return [2, 3, 4, 5]
  }

  if (current >= total - 2) {
    return [total - 4, total - 3, total - 2, total - 1]
  }

  return [current - 1, current, current + 1]
}



const hasMissingRequiredFields = (team) => {
  if (!team.fields || !user.value) return false

  return team.fields.some((field) => {
    if (!field.required) return false
    

    if (field.responses && Array.isArray(field.responses)) {

      const userResponse = field.responses.find(response => response.userId === user.value.id)
      return !userResponse || !userResponse.data || userResponse.data === ''
    }
    

    if (Array.isArray(field.response)) {
      return field.response.length === 0 || !field.response[0]?.data
    }
    
    return true
  })
}


const showPendingBadge = (team) => {
  const isAdmin = team.role === 'teamAdmin' || team.creatorId === user.value?.id
  return isAdmin && team.pendingRequestsCount > 0
}


const hasPastRequests = computed(() => {
  return pastRequests.value.length > 0
})


const paginatedTeams = computed(() => {
  return teams.value
})

const loadRequests = async () => {
  const result = await teamService.getUserJoinRequests()
  requests.value = result.requests || []
}


const loadPendingRequests = async () => {
  loadingRequests.value = true
  try {
    const result = await teamService.getUserJoinRequests('pending')
    pendingRequests.value = result.requests || []
  } catch (err) {
    console.error('Fehler beim Laden der Beitrittsanfragen:', err)
  } finally {
    loadingRequests.value = false
  }
}

const loadPastRequests = async () => {
  loadingHistory.value = true
  try {
    const result = await teamService.getUserJoinRequests('rejected')
    const rejectedRequests = result.requests || []
    
    pastRequests.value = rejectedRequests
      .sort((a, b) => new Date(b.processedAt || b.requestedAt) - new Date(a.processedAt || a.requestedAt))
  } catch (err) {
    console.error('Fehler beim Laden der abgelehnten Anfragen:', err)
    showError('Abgelehnte Anfragen konnten nicht geladen werden')
  } finally {
    loadingHistory.value = false
  }
}


onMounted(async () => {
  await teamsStore.fetchTeams()
  await loadPendingRequests()
  await loadPastRequests()
  await loadRequests()
  for (const team of teams.value) {
    if (team.receiptFooterImage) {
      loadLogoIfNeeded(team.id)
    }
  }
})


onUnmounted(() => {

  logoUrls.value.forEach(url => {
    URL.revokeObjectURL(url)
  })
  logoUrls.value.clear()
})
</script>

<style scoped>
.border-dashed {
  border: 2px dashed #dee2e6 !important;
  transition: all 0.3s ease;
}

.border-dashed:hover {
  border-color: var(--bs-primary) !important;
  background-color: rgba(var(--bs-primary-rgb), 0.05) !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.border-dashed .card-body:hover {
  background-color: rgba(var(--bs-primary-rgb), 0.1) !important;
  transition: background-color 0.2s ease;
}

.border-dashed .card-body:last-child:hover {
  background-color: rgba(var(--bs-success-rgb), 0.1) !important;
}
</style>
